import { useState, useEffect } from "react";
import styled from "styled-components";
import { useSessionContext } from "../../context/session.context";
import { useVeryNotificationsContext } from "../../components/common/v2/VeryNotifications/useVeryNotifications";
import { VeryBtnFill } from "../../components/common/v2/VeryBtnFill";
import { classVeryBtnFill } from "../../components/common/v2/VeryBtnFill/classVeryBtnFill";
import { VeryInput } from "../../components/common/v2/VeryInput";
import { getAppStatus, updateAppStatus } from "../../services/appStatus";
import { DivContentPage } from "../../components/common/DivContentPage";

const Styles = {
  Page: styled(DivContentPage)`
    width: 100%;
    .page-content {
      max-width: 1200px;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 24px;
      padding-bottom: 32px;
    }
  `,
  Title: styled.h2`
    font-size: 20px;
    font-weight: 600;
    margin: 0;
  `,
  Card: styled.div`
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  `,
  ToggleRow: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  `,
  ToggleLabel: styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    .title { font-size: 15px; font-weight: 600; }
    .subtitle { font-size: 13px; color: #6b7280; }
  `,
  Switch: styled.button`
    position: relative;
    width: 52px;
    height: 28px;
    border-radius: 14px;
    border: none;
    cursor: pointer;
    background: ${(p) => (p.$active ? "#ef4444" : "#d1d5db")};
    transition: background 0.2s;
    flex-shrink: 0;
    &::after {
      content: "";
      position: absolute;
      top: 3px;
      left: ${(p) => (p.$active ? "27px" : "3px")};
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: #ffffff;
      transition: left 0.2s;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }
  `,
  FeatureSwitch: styled.button`
    position: relative;
    width: 52px;
    height: 28px;
    border-radius: 14px;
    border: none;
    cursor: pointer;
    background: ${(p) => (p.$active ? "#059669" : "#d1d5db")};
    transition: background 0.2s;
    flex-shrink: 0;
    &::after {
      content: "";
      position: absolute;
      top: 3px;
      left: ${(p) => (p.$active ? "27px" : "3px")};
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: #ffffff;
      transition: left 0.2s;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }
  `,
  StatusBadge: styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    background: ${(p) => (p.$blocked ? "#fee2e2" : "#d1fae5")};
    color: ${(p) => (p.$blocked ? "#dc2626" : "#059669")};
  `,
  Hint: styled.p`
    font-size: 12px;
    color: #9ca3af;
    margin: 0;
    line-height: 1.5;
  `,
  Separator: styled.div`
    height: 1px;
    background: #e5e7eb;
    margin: 8px 0;
  `,
  Row: styled.div`
    display: flex;
    gap: 24px;
    width: 100%;
    @media (max-width: 768px) {
      flex-direction: column;
    }
  `,
  Col: styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 24px;
  `,
};

const AppStatusPage = () => {
  const session = useSessionContext();
  const notis = useVeryNotificationsContext();
  const jwt = session.value?.jwt ?? "";

  const [blocked, setBlocked] = useState(false);
  const [message, setMessage] = useState("");
  const [searchRadius, setSearchRadius] = useState("5.0");
  const [minVersionPassengerAndroid, setMinVersionPassengerAndroid] = useState("1.0.0");
  const [minVersionPassengerIos, setMinVersionPassengerIos] = useState("1.0.0");
  const [minVersionDriverAndroid, setMinVersionDriverAndroid] = useState("1.0.0");
  const [minVersionDriverIos, setMinVersionDriverIos] = useState("1.0.0");
  const [allowTaxiCoupons, setAllowTaxiCoupons] = useState(true);
  const [allowShipmentCoupons, setAllowShipmentCoupons] = useState(true);
  const [loading, setLoading] = useState(true);
  const [savingBlocked, setSavingBlocked] = useState(false);
  const [savingRadius, setSavingRadius] = useState(false);
  const [savingVersions, setSavingVersions] = useState(false);
  const [savingCoupons, setSavingCoupons] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      setLoading(true);
      const resp = await getAppStatus();
      if (resp.error) {
        notis.createNotiError("No se pudo obtener el estado de la app", resp.error.details);
        setLoading(false);
        return;
      }
      setBlocked(resp.body.blocked === true);
      setMessage(resp.body.message ?? "");
      setSearchRadius(resp.body.searchRadius !== undefined ? resp.body.searchRadius.toString() : "5.0");
      setMinVersionPassengerAndroid(resp.body.minVersionPassengerAndroid ?? "1.0.0");
      setMinVersionPassengerIos(resp.body.minVersionPassengerIos ?? "1.0.0");
      setMinVersionDriverAndroid(resp.body.minVersionDriverAndroid ?? "1.0.0");
      setMinVersionDriverIos(resp.body.minVersionDriverIos ?? "1.0.0");
      setAllowTaxiCoupons(resp.body.allowTaxiCoupons !== false);
      setAllowShipmentCoupons(resp.body.allowShipmentCoupons !== false);
      setLoading(false);
    };
    fetchStatus();
  }, [notis]);

  const onSaveCouponsStatus = async () => {
    setSavingCoupons(true);
    const resp = await updateAppStatus({ allowTaxiCoupons, allowShipmentCoupons }, jwt);
    setSavingCoupons(false);
    if (resp.error) {
      notis.createNotiError("No se pudo actualizar la disponibilidad de cupones", resp.error.details);
      return;
    }
    notis.createNotiSuccess(
      "Disponibilidad de cupones actualizada",
      `Taxi: ${allowTaxiCoupons ? "Habilitados" : "Deshabilitados"} | Envíos: ${allowShipmentCoupons ? "Habilitados" : "Deshabilitados"}`
    );
  };

  const onSaveBlocked = async () => {
    setSavingBlocked(true);
    const resp = await updateAppStatus({ blocked, message }, jwt);
    setSavingBlocked(false);
    if (resp.error) {
      notis.createNotiError("No se pudo actualizar el estado de bloqueo", resp.error.details);
      return;
    }
    notis.createNotiSuccess(
      blocked
        ? "App bloqueada correctamente"
        : "App desbloqueada correctamente",
      blocked
        ? "Los usuarios verán el mensaje configurado al abrir la app"
        : "Los usuarios podrán usar la app con normalidad"
    );
  };

  const onSaveRadius = async () => {
    let radiusNum = parseFloat(searchRadius);
    if (isNaN(radiusNum) || radiusNum <= 0) {
      radiusNum = 5.0;
      setSearchRadius("5.0");
    }
    setSavingRadius(true);
    const resp = await updateAppStatus({ searchRadius: radiusNum }, jwt);
    setSavingRadius(false);
    if (resp.error) {
      notis.createNotiError("No se pudo actualizar el rango de búsqueda", resp.error.details);
      return;
    }
    notis.createNotiSuccess(
      "Rango de búsqueda actualizado",
      `El rango de solicitudes se configuró en ${radiusNum} km`
    );
  };

  const onSaveVersions = async () => {
    setSavingVersions(true);
    const resp = await updateAppStatus({
      minVersionPassengerAndroid,
      minVersionPassengerIos,
      minVersionDriverAndroid,
      minVersionDriverIos
    }, jwt);
    setSavingVersions(false);
    if (resp.error) {
      notis.createNotiError("No se pudieron actualizar las versiones mínimas", resp.error.details);
      return;
    }
    notis.createNotiSuccess(
      "Versiones mínimas actualizadas",
      "Las versiones mínimas requeridas se guardaron correctamente"
    );
  };

  return (
    <Styles.Page isLoading={loading}>
      <div className="page-content">
        <Styles.Title>Estado de la App</Styles.Title>

        {/* SECCIÓN 1: BLOQUEO GLOBAL (ESTILO WHITELIST CON BORDES ROJOS) */}
        <div style={{
          backgroundColor: "#ffffff",
          border: "2px solid #dc2626",
          borderRadius: "8px",
          padding: "20px",
          color: "#1f2937",
          fontSize: "14px",
          lineHeight: "1.5",
          display: "flex",
          flexDirection: "column",
          gap: "16px"
        }}>
          <div style={{ fontWeight: "600", color: "#dc2626", display: "flex", alignItems: "center", gap: "8px", fontSize: "16px" }}>
            <span className="material-symbols-outlined" style={{ fontSize: "22px" }}>warning</span>
            Advertencia de Bloqueo de Acceso Global
          </div>
          <div style={{ color: "#4b5563" }}>
            Activar el bloqueo de acceso impedirá que <strong>todos</strong> los usuarios (tanto pasajeros como conductores) inicien sesión, soliciten viajes o realicen cualquier actividad en las aplicaciones móviles, mostrándoles la pantalla de bloqueo con el mensaje configurado.
          </div>

          <div style={{ height: "1px", backgroundColor: "#e5e7eb", margin: "4px 0" }} />

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span style={{ fontWeight: "600", fontSize: "14px", color: "#1f2937" }}>Bloquear acceso global a las aplicaciones (APP_BLOCKED ON)</span>
              <span style={{ fontSize: "12px", color: "#6b7280" }}>
                Estado actual en base de datos: {blocked ? "⛔ Acceso Bloqueado (Mantenimiento activo)" : "✅ Acceso Libre (App activa)"}
              </span>
            </div>
            <Styles.Switch $active={blocked} onClick={() => setBlocked((prev) => !prev)} />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "4px" }}>
            <label style={{ fontWeight: "600", color: "#1f2937" }}>Mensaje de Bloqueo Personalizado para Usuarios:</label>
            <div style={{ display: "flex", gap: "10px", alignItems: "center", width: "100%" }}>
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ej: Estamos en mantenimiento, regresamos pronto..."
                style={{
                  flex: 1,
                  backgroundColor: "#ffffff",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  color: "#1f2937",
                  outline: "none"
                }}
              />
              <div style={{ flexShrink: 0 }}>
                <VeryBtnFill
                  label={savingBlocked ? "Guardando..." : "Guardar cambios"}
                  colorTheme={classVeryBtnFill.COLOR_THEME.BLACK_S1}
                  textSize={14}
                  isLoading={savingBlocked}
                  onClick={onSaveBlocked}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECCIÓN: CONTROL DE VERY ENVÍOS (REUBICADO A SECCIÓN WHITELIST) */}
        <div style={{
          backgroundColor: "#f0f9ff",
          border: "1px solid #bae6fd",
          borderRadius: "8px",
          padding: "16px 20px",
          color: "#0369a1",
          fontSize: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span className="material-symbols-outlined" style={{ fontSize: "24px", color: "#0284c7" }}>local_shipping</span>
            <div>
              <div style={{ fontWeight: "600", fontSize: "14px", color: "#0c4a6e" }}>Control y Lista de Acceso a Very Envíos</div>
              <div style={{ fontSize: "12px", color: "#0284c7" }}>El switch global de Very Envíos y la lista de acceso exclusivo se gestionan de forma centralizada en la pestaña <strong>Acceso a Envíos (Shipments)</strong> de la sección Whitelist.</div>
            </div>
          </div>
          <a
            href="/whitelist"
            style={{
              padding: "8px 16px",
              backgroundColor: "#0284c7",
              color: "#ffffff",
              borderRadius: "6px",
              textDecoration: "none",
              fontWeight: "600",
              fontSize: "13px",
              flexShrink: 0,
            }}
          >
            Ir a Whitelist de Envíos
          </a>
        </div>

        {/* SECCIÓN: DISPONIBILIDAD DE CUPONES POR SERVICIO */}
        <div style={{
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          padding: "20px",
          color: "#1f2937",
          fontSize: "14px",
          lineHeight: "1.5",
          display: "flex",
          flexDirection: "column",
          gap: "16px"
        }}>
          <div style={{ fontWeight: "600", color: "#111827", display: "flex", alignItems: "center", gap: "8px", fontSize: "16px" }}>
            <span className="material-symbols-outlined" style={{ fontSize: "22px", color: "#4f46e5" }}>confirmation_number</span>
            Disponibilidad de Cupones por Tipo de Servicio
          </div>
          <div style={{ color: "#4b5563" }}>
            Controla de forma independiente si los usuarios pueden canjear o aplicar cupones de descuento en viajes de Taxi y/o en el servicio de Envíos. Los viajes que ya hayan iniciado con cupón finalizarán normalmente respetando el descuento.
          </div>

          <div style={{ height: "1px", backgroundColor: "#e5e7eb", margin: "4px 0" }} />

          {/* SWITCH 1: TAXI */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span style={{ fontWeight: "600", fontSize: "14px", color: "#1f2937" }}>Permitir cupones en Viajes (Taxi)</span>
              <span style={{ fontSize: "12px", color: allowTaxiCoupons ? "#059669" : "#d97706" }}>
                Estado actual: {allowTaxiCoupons ? "✅ Cupones habilitados para viajes de Taxi" : "⛔ Cupones deshabilitados para viajes de Taxi"}
              </span>
            </div>
            <Styles.FeatureSwitch
              $active={allowTaxiCoupons}
              onClick={() => setAllowTaxiCoupons((prev) => !prev)}
            />
          </div>

          <div style={{ height: "1px", backgroundColor: "#e5e7eb", margin: "4px 0" }} />

          {/* SWITCH 2: SHIPMENTS */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span style={{ fontWeight: "600", fontSize: "14px", color: "#1f2937" }}>Permitir cupones en Envíos (Paquetería)</span>
              <span style={{ fontSize: "12px", color: allowShipmentCoupons ? "#059669" : "#d97706" }}>
                Estado actual: {allowShipmentCoupons ? "✅ Cupones habilitados para Envíos" : "⛔ Cupones deshabilitados para Envíos"}
              </span>
            </div>
            <Styles.FeatureSwitch
              $active={allowShipmentCoupons}
              onClick={() => setAllowShipmentCoupons((prev) => !prev)}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
            <VeryBtnFill
              label={savingCoupons ? "Guardando..." : "Guardar cambios de cupones"}
              colorTheme={classVeryBtnFill.COLOR_THEME.BLACK_S1}
              textSize={14}
              isLoading={savingCoupons}
              onClick={onSaveCouponsStatus}
            />
          </div>
        </div>

        {/* SECCIÓN 2: OTRAS CONFIGURACIONES EN PARALELO */}
        <Styles.Row>
          {/* Card Left: Rango de Búsqueda */}
          <Styles.Col>
            <Styles.Card style={{ height: "100%", justifyContent: "space-between" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                <Styles.Title style={{ fontSize: 16, borderBottom: "1px solid #f3f4f6", paddingBottom: 12 }}>
                  Rango de Solicitudes
                </Styles.Title>

                <VeryInput
                  title="Radio máximo de búsqueda (km)"
                  isEditable
                  placeHolder="Ej: 5.0"
                  text={searchRadius}
                  onChangeText={(v) => setSearchRadius(v)}
                />

                <Styles.Hint>
                  Define la distancia máxima en kilómetros a la redonda para asignar solicitudes de viaje y mostrar conductores disponibles.
                  Soporta decimales (ej: 0.1 para 100m, 0.01 para 10m).
                </Styles.Hint>
              </div>

              <div style={{ marginTop: "16px" }}>
                <VeryBtnFill
                  label={savingRadius ? "Guardando..." : "Guardar rango"}
                  colorTheme={classVeryBtnFill.COLOR_THEME.BLACK_S1}
                  textSize={14}
                  isLoading={savingRadius}
                  onClick={onSaveRadius}
                />
              </div>
            </Styles.Card>
          </Styles.Col>

          {/* Card Right: Control de Versiones Mínimas */}
          <Styles.Col>
            <Styles.Card style={{ height: "100%", justifyContent: "space-between" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <Styles.Title style={{ fontSize: 16, borderBottom: "1px solid #f3f4f6", paddingBottom: 12 }}>
                  Control de Versiones Mínimas
                </Styles.Title>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <span style={{ fontWeight: "600", fontSize: "13px", color: "#374151" }}>Aplicación Pasajeros</span>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <VeryInput
                      title="Mínimo Android"
                      isEditable
                      placeHolder="Ej: 1.0.0"
                      text={minVersionPassengerAndroid}
                      onChangeText={(v) => setMinVersionPassengerAndroid(v)}
                    />
                    <VeryInput
                      title="Mínimo iOS"
                      isEditable
                      placeHolder="Ej: 1.0.0"
                      text={minVersionPassengerIos}
                      onChangeText={(v) => setMinVersionPassengerIos(v)}
                    />
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <span style={{ fontWeight: "600", fontSize: "13px", color: "#374151" }}>Aplicación Conductores</span>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <VeryInput
                      title="Mínimo Android"
                      isEditable
                      placeHolder="Ej: 1.0.0"
                      text={minVersionDriverAndroid}
                      onChangeText={(v) => setMinVersionDriverAndroid(v)}
                    />
                    <VeryInput
                      title="Mínimo iOS"
                      isEditable
                      placeHolder="Ej: 1.0.0"
                      text={minVersionDriverIos}
                      onChangeText={(v) => setMinVersionDriverIos(v)}
                    />
                  </div>
                </div>

                <Styles.Hint>
                  Define las versiones mínimas de pasajeros y conductores para cada sistema operativo. Si la app del usuario es menor a la indicada para su plataforma, se le bloqueará solicitando actualizar.
                </Styles.Hint>
              </div>

              <div style={{ marginTop: "16px" }}>
                <VeryBtnFill
                  label={savingVersions ? "Guardando..." : "Guardar versiones"}
                  colorTheme={classVeryBtnFill.COLOR_THEME.BLACK_S1}
                  textSize={14}
                  isLoading={savingVersions}
                  onClick={onSaveVersions}
                />
              </div>
            </Styles.Card>
          </Styles.Col>
        </Styles.Row>
      </div>
    </Styles.Page>
  );
};

export default AppStatusPage;
